import React, { useEffect, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { ApplicationState, BoundActions } from 'store/types/common'
import { notificationActionCreators } from 'store/logic/notification-logic'
import { bindActionCreators, Dispatch } from 'redux'
import { getUnreadNotificationsCount } from 'store/selectors/notification-selectors'
import HeaderNotification from '../HeaderNotification/HeaderNotification'
import MobileMenuNotification from '../MobileMenuNotification/MobileMenuNotification'
import styles from './Notifications.module.scss'
import NotificationsList, { notificationPageSize } from '../NotificationsList/NotificationsList'
import AdminHeaderNotification from '../AdminHeaderNotification/AdminHeaderNotification'
import { NotificationType } from 'consts'
import { NotificationModel } from 'store/types/notification-types'
import { GeneralUtils } from 'server/utils/general-utils'
import { useDialogWithParameter } from 'components/_hooks'
import LobbyDetailModal from 'components/_shared/TournamentBracket/_shared/LobbyDetailModal'
import { getAppUserRole } from 'store/selectors/app-selectors'
import { useKlHistory } from 'components/_hooks/useKlHistory'
import { reLoadTopFriends } from 'store/logic/user-logic'
import { getFriendRequest, useAcceptFriendRequest } from 'store/logic/friend-request-logic'

export type NotificationIconType = 'mobile' | 'header' | 'admin'

interface OwnProps {
  notificationIconType: NotificationIconType
  isOpened: boolean
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  className?: string
  renderIcon?: (onClick: () => void, value?: number) => JSX.Element
}

interface StateProps {
  unreadNotificationsCount: number
}

type DispatchProps = BoundActions<typeof notificationActionCreators>

type Props = OwnProps & StateProps & DispatchProps

const Notifications = ({
  loadUnreadNotificationsCount,
  loadNotifications,
  unreadNotificationsCount,
  isOpened,
  setIsOpened,
  page,
  setPage,
  notificationIconType,
  className,
  renderIcon,
}: Props) => {
  const dispatch = useDispatch()

  const acceptRequest = useAcceptFriendRequest()

  const IconContainer = useMemo(() => {
    if (notificationIconType === 'mobile') {
      return MobileMenuNotification
    }
    if (notificationIconType === 'header') {
      return HeaderNotification
    }
    return AdminHeaderNotification
  }, [notificationIconType])

  const iconClassName = useMemo(() => {
    if (notificationIconType === 'mobile') {
      return styles.mobileMenuNotificationNotifications
    }
    if (notificationIconType === 'header') {
      return styles.headerNotificationNotifications
    }
    return ''
  }, [notificationIconType])

  const userRole = useSelector(getAppUserRole)
  
  const [matchSettingsModal, openMatchSettingsModal] = useDialogWithParameter<NotificationModel>((notification, closeDialog) => (
    <LobbyDetailModal onClose={closeDialog} matchId={notification.SubjectId} />
  ))  
  

  const handleClick = () => {
    setIsOpened(!isOpened)
  }

  const history = useKlHistory()

  const moveToLink = (notification: NotificationModel) => {
    if (notification.Button?.Uri) {
      history.push(notification.Button.Uri) 
    }
  }

  const processNotification = async (notification: NotificationModel) => {
    switch (notification.Type) {
      case NotificationType.SendFriendRequest: {
        try {
          const friendRequest = await getFriendRequest(notification.SubjectId)
          await acceptRequest(friendRequest.Id)

          refreshNotifications()
          dispatch(reLoadTopFriends(friendRequest.ToUserId))
          dispatch(reLoadTopFriends(friendRequest.FromUserId))       
        } catch (error) {
          GeneralUtils.showError(error)
        }
        moveToLink(notification)
        break
      }
      case NotificationType.MatchHostSelected: {
        openMatchSettingsModal(notification)
        setIsOpened(false)
        break
      }
      case NotificationType.HostSettingsReminder: {
        if (userRole == 'Player') {
          openMatchSettingsModal(notification)
          setIsOpened(false)
          break
        } else {
          moveToLink(notification)
          setIsOpened(false)
          break
        }
      }
      default: {
        moveToLink(notification)
        setIsOpened(false)
      }
    }   
  }

  const close = async (notification: NotificationModel) => {
    await processNotification(notification)
  }

  useEffect(() => {
    loadUnreadNotificationsCount()
  }, [loadUnreadNotificationsCount])

  
  const refreshNotifications = () => {
    loadNotifications(0, notificationPageSize * (page + 1))
    loadUnreadNotificationsCount()
  }
  
  return (
    <>
      {renderIcon !== undefined
        ? renderIcon(handleClick, unreadNotificationsCount)
        : <IconContainer onClick={handleClick} className={iconClassName} displayValue value={unreadNotificationsCount} />
      }
      <NotificationsList isOpened={isOpened} page={page} setPage={setPage} close={close} className={className} />
      {matchSettingsModal}
    </>
  )
}

const makeMapStateToProps = () => {
  return (state: ApplicationState, props: OwnProps): StateProps => {
    return {
      unreadNotificationsCount: getUnreadNotificationsCount(state),
    }
  }
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  ...bindActionCreators(notificationActionCreators, dispatch),
})

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(makeMapStateToProps, mapDispatchToProps)(Notifications)
